import React, { useEffect, useMemo, useState } from "react";
import { openWhatsApp } from "../../lib/whatsapp";
import axios from "axios";
import { Loader2, Users, Search, X, Download, MessageCircle, Sparkles, Heart, Flower, Award, Eye, Pencil, Trash2, Save, BedDouble, Utensils, Coffee, Plus, Upload, Megaphone, TrendingUp, BanIcon, ShieldOff, Ticket, Gift, RotateCcw, MoreHorizontal, RefreshCw, Merge } from "lucide-react";
import {
  adminListCustomers,
  adminUpdateLoyaltyUser,
  adminDeleteLoyaltyUser,
  adminAddManualCustomer,
  adminAddManualCustomerBulk,
  adminResetAllLoyaltyBalances,
  adminLoyaltyWelcomeInvite,
  adminGrantMissingWelcome,
  adminRecomputeCashback,
  adminRedeemLoyaltyCredit, adminAddLoyaltyCredit,
  updateSpaBooking, deleteSpaBooking, cancelSpaBooking,
  updateMassageBooking, deleteMassageBooking, cancelMassageBooking,
  previewWaTemplate,
  logWaSend, fetchWaHistory, fetchWaStats,
  addDnc, removeDnc,
} from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import { GdprMenuItems } from "./GdprMenuItems";
import { ImportCustomersCsv } from "./ImportCustomersCsv";
import { CustomerAutocomplete } from "../CustomerAutocomplete";
import { MergeCustomersDialog, DuplicatesDialog } from "./MergeCustomers";
import { CampaignPanel } from "./CampaignPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}` });

const SOURCE_META = {
  fidelity: { label: "Fidelity", cls: "bg-amber-100 text-amber-800", Icon: Award },
  spa:      { label: "Spa", cls: "bg-emerald-100 text-emerald-800", Icon: Flower },
  massaggi: { label: "Massaggi", cls: "bg-pink-100 text-pink-800", Icon: Heart },
  hotel:    { label: "Hotel", cls: "bg-sky-100 text-sky-800", Icon: BedDouble },
  evento:   { label: "Evento", cls: "bg-violet-100 text-violet-800", Icon: Sparkles },
  cavallo:  { label: "Cavallo", cls: "bg-lime-100 text-lime-800", Icon: Sparkles },
  ristorante: { label: "Ristorante", cls: "bg-rose-100 text-rose-800", Icon: Utensils },
  bar:      { label: "Bar", cls: "bg-orange-100 text-orange-800", Icon: Coffee },
  passaggio: { label: "Passaggio", cls: "bg-slate-100 text-slate-700", Icon: Users },
  altro:    { label: "Altro", cls: "bg-slate-100 text-slate-700", Icon: Users },
  manuale:  { label: "Manuale", cls: "bg-slate-100 text-slate-700", Icon: Users },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const s = String(iso).slice(0, 10);
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("it-IT");
  } catch { return String(iso); }
}

function fmtMoney(n) {
  const v = Number(n || 0);
  return `€ ${v.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function normalisePhoneWa(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.startsWith("00")) return digits.slice(2);
  if (digits.startsWith("39")) return digits;
  if (digits.startsWith("3") && digits.length >= 9) return "39" + digits;
  return digits;
}

const hasCredit = (c) => Number(c.rows?.find?.((r) => r.source === "fidelity")?.detail?.balance_cents || 0) > 0;

function SourceBadge({ source }) {
  const m = SOURCE_META[source];
  if (!m) return null;
  const { Icon } = m;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

export default function CustomersPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ sources: [], unified: [], counts: {} });
  const [view, setView] = useState("unified"); // unified | sources
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [waHistoryFor, setWaHistoryFor] = useState(null); // customer whose WA log is open
  const [waHistory, setWaHistory] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("all"); // all | fidelity | spa | massaggi
  const [editRow, setEditRow] = useState(null); // source row being edited
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSel, setMergeSel] = useState([]);
  const [mergeCustomers, setMergeCustomers] = useState(null);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [hideDnc, setHideDnc] = useState(false);
  const [creditOnly, setCreditOnly] = useState(false);
  const [welcomeCampaign, setWelcomeCampaign] = useState(null); // {rows: []}
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminListCustomers();
      setData(r);
      // If a detail modal is open, refresh its data too.
      if (detail) {
        const updated = (r.unified || []).find((u) => u.key === detail.key);
        if (updated) setDetail(updated); else setDetail(null);
      }
    } catch (e) {
      toast({ title: "Errore caricamento clienti", description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("customers:refresh", h);
    return () => window.removeEventListener("customers:refresh", h);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEdit = async (row, patch) => {
    try {
      if (row.source === "fidelity") {
        await adminUpdateLoyaltyUser(row.detail.loyalty_id, patch);
      } else if (row.source === "spa") {
        await updateSpaBooking(row.detail.booking_id, patch);
      } else if (row.source === "massaggi") {
        await updateMassageBooking(row.detail.booking_id, patch);
      }
      toast({ title: "Modifiche salvate" });
      setEditRow(null);
      await load();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  const deleteRow = async (row) => {
    const src = SOURCE_META[row.source]?.label || row.source;
    const label = row.source === "fidelity"
      ? `Eliminare l'iscritto Fidelity "${row.name}"?\nSi perderanno ${row.stamps || 0} timbri e tutto lo storico.`
      : `Eliminare la prenotazione ${src} del ${row.date} per "${row.name}"?`;
    if (!window.confirm(`${label}\n\nQuesta operazione non è reversibile.`)) return;
    try {
      if (row.source === "fidelity") {
        await adminDeleteLoyaltyUser(row.detail.loyalty_id);
      } else if (row.source === "spa") {
        await deleteSpaBooking(row.detail.booking_id);
      } else if (row.source === "massaggi") {
        await deleteMassageBooking(row.detail.booking_id);
      }
      toast({ title: "Eliminato" });
      await load();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  const cancelBooking = async (row) => {
    if (row.detail?.status === "cancelled") return;
    if (!window.confirm(`Segnare la prenotazione del ${row.date} come annullata?`)) return;
    try {
      if (row.source === "spa") await cancelSpaBooking(row.detail.booking_id);
      else if (row.source === "massaggi") await cancelMassageBooking(row.detail.booking_id);
      toast({ title: "Prenotazione annullata" });
      await load();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  const [sortMode, setSortMode] = useState("name-asc"); // name-asc | name-desc | recent

  const _lastNameOf = (fullName) => {
    // Sort key: last word of the name (cognome). Fallback: whole string.
    const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
    return (parts.length > 1 ? parts[parts.length - 1] : parts[0] || "").toLowerCase();
  };

  const filteredUnified = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (data.unified || []).filter((c) => {
      if (sourceFilter !== "all" && !c.sources.includes(sourceFilter)) return false;
      if (creditOnly && !hasCredit(c)) return false;
      if (hideDnc && c.do_not_contact) return false;
      if (!needle) return true;
      return (
        (c.name || "").toLowerCase().includes(needle) ||
        (c.email || "").toLowerCase().includes(needle) ||
        (c.phone || "").toLowerCase().includes(needle)
      );
    });
    if (sortMode === "name-asc") {
      list.sort((a, b) => _lastNameOf(a.name).localeCompare(_lastNameOf(b.name)) || (a.name || "").localeCompare(b.name || ""));
    } else if (sortMode === "name-desc") {
      list.sort((a, b) => _lastNameOf(b.name).localeCompare(_lastNameOf(a.name)) || (b.name || "").localeCompare(a.name || ""));
    } else if (sortMode === "recent") {
      list.sort((a, b) => (b.last_activity || "").localeCompare(a.last_activity || ""));
    }
    return list;
  }, [data.unified, q, sourceFilter, sortMode, hideDnc, creditOnly]);



  const filteredSources = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (data.sources || []).filter((r) => {
      if (sourceFilter !== "all" && r.source !== sourceFilter) return false;
      if (!needle) return true;
      return (
        (r.name || "").toLowerCase().includes(needle) ||
        (r.email || "").toLowerCase().includes(needle) ||
        (r.phone || "").toLowerCase().includes(needle)
      );
    });
    if (sortMode === "name-asc") {
      list.sort((a, b) => _lastNameOf(a.name).localeCompare(_lastNameOf(b.name)));
    } else if (sortMode === "name-desc") {
      list.sort((a, b) => _lastNameOf(b.name).localeCompare(_lastNameOf(a.name)));
    } else if (sortMode === "recent") {
      list.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    }
    return list;
  }, [data.sources, q, sourceFilter, sortMode]);


  // Registra una spesa (cashback automatico) — auto-iscrive a Fidelity se non ancora
  const addStampsQuick = async (c) => {
    const fidelityRow = c.rows?.find?.((r) => r.source === "fidelity");
    const loyaltyId = fidelityRow?.detail?.loyalty_id || null;
    const isNew = !loyaltyId;
    const promptLabel = isNew
      ? `${c.name} non è ancora Fidelity. Quanto ha speso? (€) — verrà iscritto e riceverà 10€ di benvenuto + cashback`
      : `Quanto ha speso ${c.name}? (€)`;
    const raw = window.prompt(promptLabel, "");
    if (raw == null || raw.trim() === "") return;
    const eur = parseFloat(raw.replace(",", "."));
    if (!Number.isFinite(eur) || eur <= 0 || eur > 10000) {
      toast({ title: "Importo non valido", description: "Inserisci un numero tra 0.01 e 10000" });
      return;
    }
    if (isNew && !c.email && !c.phone) {
      toast({ title: "Impossibile iscrivere", description: "Al cliente serve almeno email o telefono. Aggiornalo dal dettaglio." });
      return;
    }
    const note = window.prompt("Nota (opzionale)", "Spesa registrata da admin");
    try {
      const payload = loyaltyId
        ? { user_id: loyaltyId, amount_euros: eur, note: note || undefined }
        : { amount_euros: eur, note: note || undefined, name: c.name, email: c.email || "", phone: c.phone || "" };
      const r = await axios.post(
        `${API}/admin/loyalty/add-purchase`,
        payload,
        { headers: authHeaders() },
      );
      const d = r.data || {};
      toast({
        title: d.auto_registered
          ? `🎫 ${c.name} iscritto (10€ welcome + ${d.cashback_euros}€ cashback)`
          : `+${d.cashback_euros}€ credito a ${c.name} (spesa ${d.amount_spent_euros}€ · ${d.cashback_pct}%)`,
        description: d.level_up
          ? `🎉 Level up: ${d.new_level?.label_it} — saldo ${d.balance_after_euros}€`
          : `Nuovo saldo: ${d.balance_after_euros}€ · Tessera Wallet sincronizzata`,
      });
      await load();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  // Register a redemption (customer used part of their credit on a bill)
  const addCreditQuick = async (c) => {
    const fidelityRow = c.rows?.find?.((r) => r.source === "fidelity");
    const loyaltyId = fidelityRow?.detail?.loyalty_id || null;
    if (!loyaltyId) { toast({ title: "Non iscritto", description: "Prima iscrivi il cliente alla Fidelity (bottone 10€ o € +)." }); return; }
    const raw = window.prompt(`Quanto credito vuoi caricare sulla tessera di ${c.name}? (€)\nLo potrà usare a pezzi, fino a esaurimento.`, "");
    if (raw == null || raw.trim() === "") return;
    const eur = parseFloat(raw.replace(",", "."));
    if (!Number.isFinite(eur) || eur <= 0 || eur > 5000) { toast({ title: "Importo non valido" }); return; }
    const note = window.prompt("Motivo (lo vede anche il cliente)", "Credito omaggio");
    if (note == null) return;
    try {
      const r = await adminAddLoyaltyCredit(loyaltyId, eur, note.trim() || "Credito omaggio");
      toast({ title: `Caricati € ${eur.toFixed(2)} a ${c.name}`, description: `Saldo tessera: € ${r.balance_after_euros.toFixed(2)}` });
      load();
    } catch (e) { toast({ title: "Errore", description: e.response?.data?.detail || e.message }); }
  };
  const redeemCreditQuick = async (c) => {
    const fidelityRow = c.rows?.find?.((r) => r.source === "fidelity");
    const loyaltyId = fidelityRow?.detail?.loyalty_id || null;
    if (!loyaltyId) {
      toast({ title: "Non iscritto", description: "Il cliente non è ancora nel programma Fidelity." });
      return;
    }
    const balanceCents = Number(fidelityRow?.detail?.balance_cents || 0);
    if (balanceCents <= 0) {
      toast({ title: "Nessun credito disponibile" });
      return;
    }
    const balanceEur = (balanceCents / 100).toFixed(2);
    const raw = window.prompt(
      `Quanto sconto vuoi applicare al conto di ${c.name}?\nCredito disponibile: € ${balanceEur}`,
      balanceEur,
    );
    if (raw == null || raw.trim() === "") return;
    const eur = parseFloat(raw.replace(",", "."));
    if (!Number.isFinite(eur) || eur <= 0) {
      toast({ title: "Importo non valido" });
      return;
    }
    if (eur > balanceCents / 100 + 0.001) {
      toast({ title: "Credito insufficiente", description: `Disponibile: € ${balanceEur}` });
      return;
    }
    const note = window.prompt("Nota (opzionale)", "Sconto applicato al conto");
    try {
      const r = await adminRedeemLoyaltyCredit(loyaltyId, eur, note || undefined);
      toast({
        title: `−€ ${eur.toFixed(2)} scontati a ${c.name}`,
        description: `Nuovo saldo: € ${r.balance_after_euros} · notifica push inviata al cliente`,
      });
      await load();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  // Delete a Fidelity subscription (removes € balance and history)
  const deleteFidelity = async (c, loyaltyId) => {
    if (!loyaltyId) return;
    if (!window.confirm(
      `Eliminare l'iscrizione Fidelity di ${c.name}?\n\n` +
      `Si perderanno il saldo (€), lo storico transazioni, le eventuali redemption e tutti i codici.\n` +
      `Le prenotazioni Spa/Massaggi/Eventi restano invariate.`
    )) return;
    try {
      await adminDeleteLoyaltyUser(loyaltyId);
      toast({ title: `Iscrizione Fidelity di ${c.name} eliminata` });
      await load();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  const openWhatsApp = async (row, templateKey = "customer_generic") => {
    const phone = normalisePhoneWa(row.phone);
    if (!phone) { toast({ title: "Numero non valido" }); return; }
    if (row.do_not_contact) {
      toast({
        title: "Cliente in lista Non-Contattare",
        description: row.dnc_reason ? `Motivo: ${row.dnc_reason}` : "Rimuovi il flag prima di inviare",
      });
      return;
    }
    const first_name = (row.name || "").split(" ")[0] || "";
    let msg;
    try {
      const r = await previewWaTemplate(templateKey, null, { name: row.name || "", first_name });
      msg = r.rendered;
    } catch (_) {
      msg = `Ciao ${first_name}! 👋`;
    }
    try {
      await logWaSend({ phone: row.phone, email: row.email, name: row.name, template_key: templateKey });
    } catch (_) { /* silent — never block a real WA send on our log */ }
    openWhatsApp(phone, msg);
    load();
  };

  // Guess primary source for the fidelity welcome template variant.
  const _primarySource = (c) => {
    const s = c.sources || [];
    if (s.includes("hotel")) return "hotel";
    if (s.includes("spa")) return "spa";
    if (s.includes("massaggi")) return "massaggi";
    if (s.includes("evento")) return "evento";
    return "";
  };

  // Send a fidelity welcome WhatsApp to a single customer (opens wa.me with
  // pre-rendered text matching their source). Also creates the loyalty
  // account with 10€ if missing, or refreshes the welcome timestamp.
  const sendFidelityWelcome = async (c) => {
    if (!c.phone) { toast({ title: "Numero WhatsApp mancante" }); return; }
    if (c.do_not_contact) { toast({ title: "Cliente in Non-Contattare" }); return; }
    if (!c.name) { toast({ title: "Nome mancante", description: "Aggiungi almeno il nome prima di inviare" }); return; }
    try {
      const r = await adminLoyaltyWelcomeInvite({
        name: c.name,
        phone: c.phone,
        email: c.email || "",
        source: _primarySource(c),
      });
      if (!r.wa_link) { toast({ title: "Numero non valido per WhatsApp" }); return; }
      try {
        await logWaSend({ phone: c.phone, email: c.email, name: c.name, template_key: r.template_key });
      } catch (_) { /* silent */ }
      openWhatsApp(r.wa_link);
      if (r.credit_added) {
        toast({ title: `🎁 ${c.name} ha ora € ${r.credit_euros} di credito` });
      } else {
        toast({ title: `WhatsApp aperto · saldo attuale € ${r.credit_euros} (welcome già assegnato in precedenza)` });
      }
      load();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  // Bulk: reset ALL fidelity balances to 10€, then open a campaign modal
  // listing every customer with WhatsApp so the admin can click-through.
  const startWelcomeCampaign = async () => {
    if (!window.confirm(
      "Azzerare tutti i saldi Fidelity e ripartire da 10€ per ogni cliente iscritto?\n\n" +
      "Poi si aprirà una lista con tutti i clienti (fidelity + spa + massaggi + hotel + manuali) " +
      "con un tasto WhatsApp per riga: cliccandolo si iscrive il cliente al Fidelity (se non è ancora) " +
      "con 10€ di credito e invii il messaggio di benvenuto adatto (pernotto / spa / massaggio / generico)."
    )) return;
    setResetBusy(true);
    try {
      const r = await adminResetAllLoyaltyBalances();
      toast({ title: `Reset completato: ${r.reset_users} tessere · +${r.welcome_credit_euros}€ ognuna` });
      // Build a candidate list from unified customers with a phone number.
      const rows = (data.unified || []).filter((c) => c.phone && !c.do_not_contact && c.name);
      setWelcomeCampaign({ rows, sent: {}, credit: r.welcome_credit_euros });
      await load();
    } catch (e) {
      toast({ title: "Errore reset", description: e?.response?.data?.detail || e.message });
    } finally { setResetBusy(false); }
  };

  // One-shot backfill: credit €10 to loyalty users whose welcome message was
  // sent but whose balance wasn't topped-up (pre-fix bug).
  const grantMissingWelcome = async () => {
    if (!window.confirm("Accreditare 10€ retroattivi ai clienti che hanno ricevuto il messaggio di benvenuto ma non il credito?")) return;
    try {
      const r = await adminGrantMissingWelcome();
      if ((r.fixed_count || 0) === 0) {
        toast({ title: "Tutto in regola", description: "Nessun cliente da recuperare" });
      } else {
        toast({ title: `+10€ accreditati a ${r.fixed_count} client${r.fixed_count === 1 ? "e" : "i"}` });
      }
      load();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  // Retroactive fix: re-price every past `earn` transaction using the tier
  // the customer REACHED with that spend (fixes the "Antonio 3% instead of 5%" bug).
  const recomputeCashback = async () => {
    if (!window.confirm(
      "Ricalcolare tutti i cashback passati con la percentuale del livello raggiunto (non quello precedente)?\n\n" +
      "Esempio: chi ha speso 200€ da Seme ora prende il 5% (Germoglio) = 10€ invece del 3% (Seme) = 6€.\n\n" +
      "Il saldo di ogni cliente verrà corretto della differenza."
    )) return;
    try {
      const r = await adminRecomputeCashback();
      if ((r.fixed_count || 0) === 0) {
        toast({ title: "Tutti i cashback erano già corretti" });
      } else {
        toast({ title: `Ricalcolati ${r.fixed_count} client${r.fixed_count === 1 ? "e" : "i"}`, description: r.fixed.slice(0, 3).map((f) => `${f.name}: +€ ${f.delta_euros}`).join(" · ") });
      }
      load();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  const sendCampaignRow = async (c) => {
    try {
      const r = await adminLoyaltyWelcomeInvite({
        name: c.name,
        phone: c.phone,
        email: c.email || "",
        source: _primarySource(c),
      });
      if (!r.wa_link) { toast({ title: "Numero non valido" }); return; }
      try { await logWaSend({ phone: c.phone, email: c.email, name: c.name, template_key: r.template_key }); } catch (_) {}
      openWhatsApp(r.wa_link);
      setWelcomeCampaign((wc) => wc ? { ...wc, sent: { ...wc.sent, [c.key]: true } } : wc);
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };


  const toggleDnc = async (row) => {
    if (row.do_not_contact) {
      if (!confirm(`Rimuovere ${row.name || "questo cliente"} dalla lista Non-Contattare?`)) return;
      try {
        await removeDnc({ phone: row.phone, email: row.email });
        toast({ title: "Rimosso da Non-Contattare" });
      } catch (e) {
        toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
        return;
      }
    } else {
      const reason = prompt(`Aggiungere ${row.name || "questo cliente"} alla lista Non-Contattare?\n\nMotivo (facoltativo):`, "");
      if (reason === null) return; // cancelled
      try {
        await addDnc({ phone: row.phone, email: row.email, name: row.name, reason });
        toast({ title: "Aggiunto a Non-Contattare" });
      } catch (e) {
        toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
        return;
      }
    }
    load();
  };

  const exportCsv = () => {
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const today = new Date().toISOString().slice(0, 10);
    let filename, header, lines;
    if (view === "unified") {
      filename = `clienti-unificati_${today}.csv`;
      header = ["Nome", "Email", "Telefono", "Fonti", "Visite spa/massaggi", "Spesa totale €", "Timbri fidelity", "Livello", "Prima attività", "Ultima attività"];
      lines = [header.join(";")];
      filteredUnified.forEach((c) => {
        lines.push([
          c.name, c.email, c.phone, c.sources.join(" · "),
          c.visits, c.total_spend.toFixed(2).replace(".", ","),
          c.stamps, c.level_it, fmtDate(c.first_activity), fmtDate(c.last_activity),
        ].map(escape).join(";"));
      });
    } else {
      filename = `clienti-per-fonte_${today}.csv`;
      header = ["Fonte", "Data", "Nome", "Email", "Telefono", "Persone", "Importo €", "Timbri", "Livello"];
      lines = [header.join(";")];
      filteredSources.forEach((r) => {
        lines.push([
          SOURCE_META[r.source]?.label || r.source, fmtDate(r.date),
          r.name, r.email, r.phone, r.num_people || "",
          r.amount ? r.amount.toFixed(2).replace(".", ",") : "",
          r.stamps || "", r.level_it || "",
        ].map(escape).join(";"));
      });
    }
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: `Esportati ${view === "unified" ? filteredUnified.length : filteredSources.length} record` });
  };

  if (loading) {
    return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>;
  }

  const c = data.counts || {};

  return (
    <div className="space-y-4" data-testid="admin-customers-panel">
      {/* Campaign performance widget */}
      <WaCampaignWidget />

      {/* Header stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <StatCard label="Clienti unici" value={c.unique_customers || 0} />
        <StatCard label="Fidelity" value={c.fidelity || 0} tone="amber" />
        <StatCard label="Spa" value={c.spa || 0} tone="emerald" />
        <StatCard label="Massaggi" value={c.massaggi || 0} tone="pink" />
        <StatCard label="Hotel" value={c.hotel || 0} tone="sky" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* View toggle */}
        <div className="inline-flex rounded-full bg-[#faf3ee] p-1 border border-[#e8dcc8]">
          <button
            onClick={() => setView("unified")}
            data-testid="customers-view-unified"
            className={`px-3 py-1 rounded-full text-xs font-semibold ${view === "unified" ? "bg-[#4a1c17] text-white" : "text-[#4a1c17]"}`}
          >
            Vista unificata
          </button>
          <button
            onClick={() => setView("sources")}
            data-testid="customers-view-sources"
            className={`px-3 py-1 rounded-full text-xs font-semibold ${view === "sources" ? "bg-[#4a1c17] text-white" : "text-[#4a1c17]"}`}
          >
            Per fonte
          </button>
        </div>
        {/* Source filter */}
        <select
          value={creditOnly ? "credit" : sourceFilter}
          onChange={(e) => { const v = e.target.value; setCreditOnly(v === "credit"); setSourceFilter(v === "credit" ? "fidelity" : v); }}
          data-testid="customers-source-filter"
          className="rounded-full border border-[#e8dcc8] bg-white text-xs px-3 py-1.5"
        >
          <option value="all">Tutte le fonti</option>
          <option value="fidelity">Solo Fidelity</option>
          <option value="credit">Solo con Credito</option>
          <option value="hotel">Solo Hotel</option>
          <option value="spa">Solo Spa</option>
          <option value="massaggi">Solo Massaggi</option>
          <option value="ristorante">Solo Ristorante</option>
          <option value="evento">Solo Eventi</option>
          <option value="cavallo">Solo Cavallo</option>
          <option value="bar">Solo Bar</option>
          <option value="passaggio">Solo Passaggio</option>
          <option value="manuale">Solo Manuali</option>
          <option value="altro">Solo Altro</option>
        </select>
        {/* Aggiungi cliente */}
        <button
          onClick={() => setShowAddDialog(true)}
          className="ml-auto sm:ml-2 inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-3 py-1.5 text-xs font-semibold"
          data-testid="customer-add-btn"
        >
          <Plus className="h-3.5 w-3.5" /> Aggiungi
        </button>
        <button onClick={() => setCampaignOpen(true)} className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-3 py-1.5 text-xs font-semibold" title="Campagna WhatsApp sulla lista clienti: 15 al giorno, per segmento, con risposte" data-testid="campaign-open-btn">
          <Megaphone className="h-3.5 w-3.5" /> Campagna clienti
        </button>
        {/* Campagna benvenuto Fidelity (reset + WA) */}
        <button
          onClick={startWelcomeCampaign}
          disabled={resetBusy}
          className="inline-flex items-center gap-1 rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-3 py-1.5 text-xs font-semibold"
          title="Azzera tutti i saldi Fidelity, ricarica 10€ a ognuno e apri la campagna WhatsApp di benvenuto"
          data-testid="welcome-campaign-btn"
        >
          {resetBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Gift className="h-3.5 w-3.5" />}
          Reset & campagna benvenuto
        </button>
        <button
          onClick={grantMissingWelcome}
          className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] hover:bg-[#faf3ee] text-[#4a1c17] px-3 py-1.5 text-xs font-semibold"
          title="Accredita 10€ retroattivi a chi ha ricevuto il messaggio ma non il credito"
          data-testid="grant-missing-welcome-btn"
        >
          <Gift className="h-3.5 w-3.5" /> Recupera welcome
        </button>
        <button
          onClick={recomputeCashback}
          className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] hover:bg-[#faf3ee] text-[#4a1c17] px-3 py-1.5 text-xs font-semibold"
          title="Ricalcola tutti i cashback passati usando il livello raggiunto (non quello precedente)"
          data-testid="recompute-cashback-btn"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Ricalcola cashback
        </button>
        {/* Search */}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-[#7c5a4e] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome, email o telefono…"
            data-testid="customers-search"
            className="w-full rounded-full border border-[#e8dcc8] bg-white pl-9 pr-9 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/20"
          />
          {q && (
            <button onClick={() => setQ("")} aria-label="Cancella" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#7c5a4e]">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <ImportCustomersCsv onDone={load} />
        <button
          onClick={() => { setMergeMode((m) => !m); setMergeSel([]); }}
          data-testid="merge-mode-btn"
          className={`inline-flex items-center gap-1.5 rounded-full text-sm px-4 py-1.5 border ${mergeMode ? "bg-amber-600 text-white border-amber-600" : "border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee]"}`}
          title="Seleziona due o più schede dello stesso cliente e uniscile"
        >
          <Merge className="h-4 w-4" /> {mergeMode ? "Esci da unione" : "Unisci doppioni"}
        </button>
        <button onClick={() => setShowDuplicates(true)} data-testid="duplicates-btn" className="inline-flex items-center gap-1.5 rounded-full text-sm px-4 py-1.5 border border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee]" title="Trova schede con lo stesso nome ma contatti diversi">
          <Search className="h-4 w-4" /> Trova doppioni
        </button>
        <button
          onClick={exportCsv}
          data-testid="customers-export-csv"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white text-sm px-4 py-1.5"
        >
          <Download className="h-4 w-4" /> Esporta CSV
        </button>
      </div>

      {view === "unified"
        ? <UnifiedTable rows={filteredUnified} total={data.unified.length} onDetail={setDetail} onWhatsApp={openWhatsApp} onFidelityWelcome={sendFidelityWelcome} onRedeemCredit={redeemCreditQuick} onAddCredit={addCreditQuick} onToggleDnc={toggleDnc} sortMode={sortMode} setSortMode={setSortMode} onEdit={setEditingCustomer} onDeleteFidelity={deleteFidelity} onAddStamps={addStampsQuick} onWaHistory={async (c) => {
            setWaHistoryFor(c);
            try {
              const r = await fetchWaHistory({ phone: c.phone, email: c.email });
              setWaHistory(r.history || []);
            } catch (e) { setWaHistory([]); }
          }} hideDnc={hideDnc} setHideDnc={setHideDnc} mergeMode={mergeMode} mergeSel={mergeSel} onToggleMerge={(key) => setMergeSel((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]))} />
        : <SourcesTable
            rows={filteredSources}
            total={data.sources.length}
            onWhatsApp={openWhatsApp}
            editRow={editRow}
            onStartEdit={setEditRow}
            onCancelEdit={() => setEditRow(null)}
            onSaveEdit={saveEdit}
            onDelete={deleteRow}
            onCancelBooking={cancelBooking}
          />
      }

      {detail && <DetailModal
        customer={detail}
        onClose={() => setDetail(null)}
        editRow={editRow}
        onStartEdit={setEditRow}
        onCancelEdit={() => setEditRow(null)}
        onSaveEdit={saveEdit}
        onDelete={deleteRow}
        onCancelBooking={cancelBooking}
      />}
      {showAddDialog && <AddCustomerDialog onClose={() => setShowAddDialog(false)} onSaved={load} />}
      {editingCustomer && <AddCustomerDialog initial={editingCustomer} onClose={() => setEditingCustomer(null)} onSaved={load} />}
      {mergeMode && mergeSel.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 rounded-full bg-[#2b1410] text-white shadow-xl px-4 py-2 flex items-center gap-3 text-sm" data-testid="merge-bar">
          <span>{mergeSel.length} selezionat{mergeSel.length === 1 ? "a" : "e"}</span>
          <button onClick={() => setMergeSel([])} className="text-xs underline opacity-80">svuota</button>
          <button disabled={mergeSel.length < 2} onClick={() => setMergeCustomers((data.unified || []).filter((c) => mergeSel.includes(c.key)))} className="rounded-full bg-amber-500 text-[#2b1410] px-3 py-1 text-xs font-bold disabled:opacity-40 inline-flex items-center gap-1" data-testid="merge-open-btn"><Merge className="h-3.5 w-3.5" /> Unisci {mergeSel.length >= 2 ? mergeSel.length : ""}</button>
        </div>
      )}
      {mergeCustomers && <MergeCustomersDialog customers={mergeCustomers} onClose={() => setMergeCustomers(null)} onDone={() => { setMergeCustomers(null); setMergeSel([]); setMergeMode(false); setShowDuplicates(false); load(); }} />}
      {showDuplicates && <DuplicatesDialog onClose={() => setShowDuplicates(false)} onMerge={(items) => setMergeCustomers(items)} />}
      {waHistoryFor && <WaHistoryModal customer={waHistoryFor} history={waHistory} onClose={() => { setWaHistoryFor(null); setWaHistory([]); }} />}
      {campaignOpen && <CampaignPanel onClose={() => { setCampaignOpen(false); }} />}
      {welcomeCampaign && <WelcomeCampaignModal campaign={welcomeCampaign} onSend={sendCampaignRow} onClose={() => setWelcomeCampaign(null)} />}
    </div>
  );
}

function WelcomeCampaignModal({ campaign, onSend, onClose }) {
  const { rows, sent, credit } = campaign;
  const total = rows.length;
  const sentCount = Object.keys(sent).length;
  const [filter, setFilter] = useState("all"); // all | pending | sent

  const filtered = rows.filter((r) => {
    if (filter === "pending") return !sent[r.key];
    if (filter === "sent") return sent[r.key];
    return true;
  });

  const _srcLabel = (c) => {
    if (c.sources?.includes("hotel")) return "Pernotto";
    if (c.sources?.includes("spa")) return "Spa";
    if (c.sources?.includes("massaggi")) return "Massaggio";
    if (c.sources?.includes("evento")) return "Evento";
    return "Generico";
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="welcome-campaign-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-amber-600 text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="font-serif text-xl font-bold leading-tight flex items-center gap-2">
              <Gift className="h-5 w-5" /> Campagna benvenuto Fidelity
            </div>
            <div className="text-xs opacity-90 mt-0.5">
              {sentCount} / {total} inviati · ogni cliente riceve € {credit} di credito omaggio
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" aria-label="Chiudi">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-[#e8dcc8] bg-[#faf3ee] flex items-center gap-2 text-xs">
          <div className="inline-flex rounded-full bg-white p-0.5 border border-[#e8dcc8]">
            {["all", "pending", "sent"].map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`px-3 py-1 rounded-full font-semibold ${filter === k ? "bg-[#4a1c17] text-white" : "text-[#4a1c17]"}`}
              >
                {k === "all" ? `Tutti (${total})` : k === "pending" ? `Da inviare (${total - sentCount})` : `Inviati (${sentCount})`}
              </button>
            ))}
          </div>
          <div className="ml-auto text-[#7c5a4e]">
            Il testo del messaggio dipende dalla fonte del cliente (Pernotto · Spa · Massaggio · Generico) — è modificabile in <em>Messaggi</em>.
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-[#7c5a4e] py-10">Nessun cliente in questa vista.</div>
          ) : filtered.map((c) => {
            const isSent = !!sent[c.key];
            return (
              <div key={c.key} className={`flex items-center gap-3 p-2.5 rounded-lg border ${isSent ? "bg-emerald-50 border-emerald-200" : "bg-white border-[#e8dcc8]"}`} data-testid={`campaign-row-${c.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#2b1410] truncate">{c.name}</div>
                  <div className="text-[11px] text-[#7c5a4e] truncate">
                    📞 {c.phone} · <span className="font-semibold text-amber-800">{_srcLabel(c)}</span>
                  </div>
                </div>
                <button
                  onClick={() => onSend(c)}
                  className={`inline-flex items-center gap-1 rounded-full text-xs font-semibold px-3 py-1.5 ${
                    isSent
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-[#4a1c17] hover:bg-[#2b1410] text-white"
                  }`}
                  data-testid={`campaign-send-${c.key}`}
                  title={isSent ? "Riapri WhatsApp per rispedire" : "Assegna 10€ + apri WhatsApp con testo pre-compilato"}
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  {isSent ? "Reinvia" : "Invia"}
                </button>
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-[#e8dcc8] bg-[#faf3ee] text-[11px] text-[#7c5a4e] flex justify-between items-center">
          <span>Suggerimento: apri i messaggi in sequenza — dopo l'invio la riga diventa verde.</span>
          <button onClick={onClose} className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white text-xs px-4 py-1.5 font-semibold">
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

function WaHistoryModal({ customer, history, onClose }) {
  const TEMPLATE_LABELS = {
    customer_generic: "Messaggio generico",
    promo: "Promo",
    fidelity_code: "Fidelity",
    spa_booking: "Spa",
    massage_booking: "Massaggio",
    room_pin_guest: "Codice camera",
    ttlock_guest: "PIN TTLock",
    ttlock_admin: "PIN admin",
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="wa-history-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#4a1c17] text-[#f7ebdf] px-4 py-3 flex items-center justify-between">
          <div>
            <div className="font-serif text-lg font-bold leading-tight">Storico WhatsApp</div>
            <div className="text-xs opacity-80">{customer.name || customer.phone}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#f7ebdf]/10 rounded" aria-label="Chiudi">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto">
          {history.length === 0 ? (
            <div className="text-center text-sm text-[#7c5a4e] py-8">Nessun messaggio inviato ancora.</div>
          ) : (
            <ul className="space-y-2" data-testid="wa-history-list">
              {history.map((h) => {
                const dt = new Date(h.sent_at);
                const label = TEMPLATE_LABELS[h.template_key] || h.template_key || "—";
                return (
                  <li key={h.id} className="flex items-center justify-between border border-[#e8dcc8] rounded-lg px-3 py-2 text-sm">
                    <div>
                      <div className="font-semibold text-[#2b1410]">{label}</div>
                      <div className="text-[11px] text-[#7c5a4e]">{dt.toLocaleString("it-IT")}</div>
                    </div>
                    <MessageCircle className="h-4 w-4 text-emerald-600" />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="px-4 py-2 border-t border-[#e8dcc8] text-[11px] text-[#7c5a4e] text-right">
          {history.length} invi{history.length === 1 ? "o" : "i"} totali
        </div>
      </div>
    </div>
  );
}

function AddCustomerDialog({ onClose, onSaved, initial = null }) {
  const [mode, setMode] = useState(initial ? "single" : "single"); // edit → single only
  const [name, setName] = useState(initial?.name || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [email, setEmail] = useState(initial?.email && !String(initial.email).endsWith("@pozzo.local") ? initial.email : "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [sourceTag, setSourceTag] = useState(initial?.source_tag || "ristorante");
  const [bulkText, setBulkText] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      if (mode === "single") {
        if (!name.trim()) { toast({ title: "Nome obbligatorio" }); return; }
        if (!phone.trim() && !email.trim()) { toast({ title: "Almeno telefono o email" }); return; }
        if (initial?.loyalty_id) {
          await adminUpdateLoyaltyUser(initial.loyalty_id, {
            name: name.trim(),
            email: email.trim() || null,
            phone: phone.trim() || null,
          });
          toast({ title: "Cliente Fidelity aggiornato" });
        } else {
          // Non-Fidelity edit: store as a manual override that wins the merge
          // (so name changes stick even for Hotel/Spa/Massaggi/Evento clients).
          const data = await adminAddManualCustomer({
            name, phone, email, source_tag: sourceTag, notes,
            admin_override: !!initial,   // true only on edit, not on new-create
          });
          toast({ title: initial ? "Cliente aggiornato" : (data.updated ? "Cliente aggiornato" : "Cliente aggiunto") });
        }
      } else {
        if (!bulkText.trim()) { toast({ title: "Incolla almeno una riga" }); return; }
        const data = await adminAddManualCustomerBulk({
          text: bulkText, source_tag: sourceTag,
        });
        toast({ title: `Aggiunti ${data.added}, aggiornati ${data.updated}, saltati ${data.skipped}` });
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast({ title: "Errore salvataggio", description: typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join(" · ") : e.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-xl font-bold text-[#2b1410]">{initial ? "Modifica cliente" : "Aggiungi cliente"}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-[#7c5a4e]" /></button>
        </div>
        <div className={`flex gap-1 rounded-full bg-[#faf7f2] p-1 mb-4 ${initial ? "hidden" : ""}`}>
          <button onClick={() => setMode("single")} className={`flex-1 px-3 py-1 rounded-full text-xs font-semibold ${mode === "single" ? "bg-[#4a1c17] text-white" : "text-[#4a1c17]"}`}>Singolo</button>
          <button onClick={() => setMode("bulk")} className={`flex-1 px-3 py-1 rounded-full text-xs font-semibold ${mode === "bulk" ? "bg-[#4a1c17] text-white" : "text-[#4a1c17]"}`}>
            <Upload className="h-3 w-3 inline mr-0.5" /> Bulk
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#7c5a4e] block mb-1">Fonte</label>
            <select value={sourceTag} onChange={(e) => setSourceTag(e.target.value)} className="w-full rounded border border-[#e8dcc8] px-2 py-1.5 text-sm">
              <option value="ristorante">Ristorante</option>
              <option value="bar">Bar</option>
              <option value="passaggio">Passaggio</option>
              <option value="altro">Altro</option>
            </select>
          </div>
          {mode === "single" ? (
            <>
              <div>
                <label className="text-xs text-[#7c5a4e] block mb-1">Nome *</label>
                <CustomerAutocomplete value={name} onChange={setName} search={(q) => axios.get(`${API}/customers/search`, { params: { q }, headers: authHeaders() }).then((r) => r.data)} placeholder="Mario Rossi" autoFocus className="w-full rounded border border-[#e8dcc8] px-2 py-1.5 text-sm" testId="add-customer-name"
                  onPick={(c) => { setName(c.name); if (c.phone) setPhone(c.phone); if (c.email) setEmail(c.email); toast({ title: "Cliente già in archivio", description: `${c.name} è già presente: evita il doppione, modifica o chiudi.` }); }} />
              </div>
              <div>
                <label className="text-xs text-[#7c5a4e] block mb-1">Telefono</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 333 1234567" className="w-full rounded border border-[#e8dcc8] px-2 py-1.5 text-sm" data-testid="add-customer-phone" />
              </div>
              <div>
                <label className="text-xs text-[#7c5a4e] block mb-1">Email (opz.)</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="mario@mail.it" className="w-full rounded border border-[#e8dcc8] px-2 py-1.5 text-sm" data-testid="add-customer-email" />
              </div>
              <div>
                <label className="text-xs text-[#7c5a4e] block mb-1">Note (opz.)</label>
                <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="es. Tavolo x4 · Cena 09/09" className="w-full rounded border border-[#e8dcc8] px-2 py-1.5 text-sm" data-testid="add-customer-notes" />
              </div>
            </>
          ) : (
            <div>
              <label className="text-xs text-[#7c5a4e] block mb-1">Incolla una riga per cliente</label>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={9}
                placeholder="Andrea Costantini · +39 347 377 6462 · ingcost@libero.it&#10;Mario Rossi | 3331234567&#10;Giulia Verdi · 3392222222"
                className="w-full rounded border border-[#e8dcc8] px-2 py-2 text-sm font-mono"
                data-testid="add-customer-bulk"
              />
              <div className="text-[10px] text-[#7c5a4e] mt-1">
                Separatori accettati: <code>·</code> <code>|</code> <code>,</code> <code>;</code> tab. Colonne: Nome · Telefono · Email(opz).
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-[#e8dcc8] px-4 py-1.5 text-sm">Annulla</button>
          <button onClick={save} disabled={busy} className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-1.5 text-sm font-semibold disabled:opacity-40" data-testid="add-customer-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const cls = tone === "amber" ? "text-amber-800" : tone === "emerald" ? "text-emerald-800" : tone === "pink" ? "text-pink-800" : tone === "sky" ? "text-sky-800" : "text-[#4a1c17]";
  return (
    <div className="rounded-2xl border border-[#e8dcc8] bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-[#7c5a4e] font-semibold">{label}</div>
      <div className={`text-xl font-bold ${cls}`}>{value}</div>
    </div>
  );
}

function UnifiedTable({ rows, total, onDetail, onWhatsApp, onFidelityWelcome, onRedeemCredit, onAddCredit, onToggleDnc, sortMode, setSortMode, onEdit, onDeleteFidelity, onWaHistory, hideDnc, setHideDnc, onAddStamps, mergeMode = false, mergeSel = [], onToggleMerge }) {
  if (rows.length === 0) {
    return <div className="rounded-2xl border border-dashed border-[#e8dcc8] p-10 text-center text-sm text-[#7c5a4e]">Nessun cliente trovato.</div>;
  }
  return (
    <div className="rounded-2xl border border-[#e8dcc8] bg-white overflow-hidden">
      <div className="px-3 py-2 bg-[#faf3ee] text-[10px] uppercase tracking-wider text-[#7c5a4e] font-semibold flex justify-between items-center gap-3 flex-wrap">
        <span>{rows.length} client{rows.length === 1 ? "e" : "i"} unic{rows.length === 1 ? "o" : "i"} · dedup su telefono/email</span>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-1.5 normal-case tracking-normal text-[11px] font-normal text-[#4a1c17] cursor-pointer" data-testid="hide-dnc-toggle">
            <input type="checkbox" checked={hideDnc} onChange={(e) => setHideDnc(e.target.checked)} className="accent-[#4a1c17]" />
            Nascondi Non-Contattare
          </label>
          {total !== rows.length && <span>filtrati da {total}</span>}
        </div>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[#7c5a4e] uppercase text-[10px] tracking-wider">
          <tr>
            <th className="text-left px-3 py-2">
              <button
                onClick={() => setSortMode(sortMode === "name-asc" ? "name-desc" : sortMode === "name-desc" ? "recent" : "name-asc")}
                className="inline-flex items-center gap-1 uppercase hover:text-[#4a1c17]"
                data-testid="sort-toggle-name"
                title="Clicca per ordinare (A-Z / Z-A / recenti)"
              >
                Cliente
                {sortMode === "name-asc" && <span className="text-[8px]">▲</span>}
                {sortMode === "name-desc" && <span className="text-[8px]">▼</span>}
                {sortMode === "recent" && <span className="text-[8px]">⏱</span>}
              </button>
            </th>
            <th className="text-left px-2 py-2 hidden md:table-cell">Contatti</th>
            <th className="text-left px-2 py-2">Fonti</th>
            <th className="text-center px-2 py-2">Visite</th>
            <th className="text-right px-2 py-2">Spesa</th>
            <th className="text-right px-2 py-2 hidden sm:table-cell" title="Credito Fidelity residuo">Credito</th>
            <th className="text-center px-2 py-2 hidden lg:table-cell">Ultima</th>
            <th className="text-right px-2 py-2">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.key} className={`border-t border-[#e8dcc8] ${c.do_not_contact ? "bg-red-50/60" : ""} ${mergeMode ? "cursor-pointer" : ""} ${mergeSel.includes(c.key) ? "bg-amber-50" : ""}`} data-testid={`customer-row-${c.key}`} onClick={mergeMode ? () => onToggleMerge(c.key) : undefined}>
              <td className="px-2 py-2">
                <div className={`font-semibold text-[13px] flex items-center gap-2 ${c.do_not_contact ? "text-red-900" : "text-[#2b1410]"}`}>
                  {mergeMode && <input type="checkbox" readOnly checked={mergeSel.includes(c.key)} className="accent-amber-600 h-4 w-4" data-testid={`merge-check-${c.key}`} />}
                  {c.name || <em className="text-[#7c5a4e]">(senza nome)</em>}
                  {c.do_not_contact && (
                    <span
                      className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-red-600 text-white text-[9px] px-1.5 py-0.5 uppercase font-semibold align-middle"
                      title={c.dnc_reason ? `Motivo: ${c.dnc_reason}` : "Non contattare"}
                      data-testid={`customer-dnc-badge-${c.key}`}
                    >
                      <BanIcon className="h-2.5 w-2.5" /> DND
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#7c5a4e] md:hidden">{c.email || c.phone}</div>
              </td>
              <td className="px-2 py-2 hidden md:table-cell text-[10px] text-[#7c5a4e]">
                {c.email && !String(c.email).endsWith("@pozzo.local") && <div className="truncate max-w-[180px]">{c.email}</div>}
                {c.phone && <div>📞 {c.phone}</div>}
              </td>
              <td className="px-2 py-2">
                <div className="flex flex-wrap gap-0.5">
                  {c.sources.map((s) => <SourceBadge key={s} source={s} />)}
                </div>
              </td>
              <td className="px-2 py-2 text-center font-mono font-bold text-[#4a1c17] text-[13px]">{c.visits}</td>
              <td className="px-2 py-2 text-right font-mono text-[#4a1c17] text-[13px]">{fmtMoney(c.total_spend)}</td>
              <td className="px-2 py-2 text-right font-mono text-[13px] hidden sm:table-cell whitespace-nowrap" data-testid={`customer-credit-${c.key}`}>
                {(() => {
                  const fid = c.rows?.find?.((r) => r.source === "fidelity");
                  if (!fid) return <span className="text-[#c9b8a4]">—</span>;
                  const bal = Number(fid.detail?.balance_cents || 0) / 100;
                  return (
                    <span className={bal > 0 ? "text-emerald-700 font-semibold" : "text-[#7c5a4e]"}>
                      € {bal.toFixed(2)}
                    </span>
                  );
                })()}
              </td>
              <td className="px-2 py-2 text-center hidden lg:table-cell text-[10px] text-[#7c5a4e] whitespace-nowrap">{fmtDate(c.last_activity)}</td>
              <td className="px-2 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                <RowActions
                  c={c}
                  onDetail={onDetail}
                  onEdit={onEdit}
                  onDeleteFidelity={onDeleteFidelity}
                  onToggleDnc={onToggleDnc}
                  onWaHistory={onWaHistory}
                  onAddStamps={onAddStamps}
                  onRedeemCredit={onRedeemCredit}
                  onAddCredit={onAddCredit}
                  onWhatsApp={onWhatsApp}
                  onFidelityWelcome={onFidelityWelcome}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

/**
 * Compact per-row action bar: primary quick actions inline
 * (€+, €−, WA, Promo, 10€) + a kebab menu (⋯) that expands
 * the secondary actions (Vedi, Modifica, Elimina Fidelity, DND).
 * Keeps the table narrow enough to fit on a laptop without clipping
 * the customer name column.
 */
function RowActions({ c, onDetail, onEdit, onDeleteFidelity, onToggleDnc, onWaHistory, onAddStamps, onRedeemCredit, onAddCredit, onWhatsApp, onFidelityWelcome }) {
  const [open, setOpen] = useState(false);
  const menuRef = React.useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!menuRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const fidRow = c.rows?.find?.((r) => r.source === "fidelity");
  const isFid = !!fidRow;
  const balCents = Number(fidRow?.detail?.balance_cents || 0);
  const canRedeem = balCents > 0;
  const addDisabled = !isFid && !c.email && !c.phone;

  const menuBtn = "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#faf3ee] text-[#2b1410]";
  const pill = "inline-flex items-center justify-center rounded-full text-[11px] font-semibold";

  return (
    <div className="inline-flex items-center gap-1">
      {c.wa_count > 0 && (
        <button
          onClick={() => onWaHistory?.(c)}
          data-testid={`customer-wa-count-${c.key}`}
          className={`${pill} bg-emerald-50 border border-emerald-300 text-emerald-800 px-2 py-1 gap-1 hover:bg-emerald-100`}
          title={c.wa_last_sent ? `Ultimo invio: ${fmtDate(c.wa_last_sent)} · ${c.wa_last_template || "—"}` : `${c.wa_count} messaggi inviati`}
        >
          <MessageCircle className="h-3 w-3" /> {c.wa_count}
        </button>
      )}

      {/* Inline Edit (always visible) + Delete Fidelity (only for iscritti) */}
      <button
        onClick={() => {
          onEdit?.({
            name: c.name,
            phone: c.phone,
            email: c.email,
            source_tag: (c.sources && c.sources[0]) || "ristorante",
            loyalty_id: fidRow?.detail?.loyalty_id || null,
          });
        }}
        data-testid={`customer-edit-${c.key}`}
        className={`${pill} bg-blue-600 hover:bg-blue-700 text-white h-7 w-7`}
        title="Modifica nome / email / telefono"
      >
        <Pencil className="h-3 w-3" />
      </button>
      {isFid && (
        <button
          onClick={() => onDeleteFidelity?.(c, fidRow.detail?.loyalty_id)}
          data-testid={`customer-delete-${c.key}`}
          className={`${pill} bg-red-600 hover:bg-red-700 text-white h-7 w-7`}
          title="Elimina iscrizione Fidelity (perde saldo e storico)"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}

      {/* Primary quick actions */}
      <button
        onClick={() => onAddStamps(c)}
        disabled={addDisabled}
        className={`${pill} text-white px-2 py-1 ${isFid ? "bg-emerald-600 hover:bg-emerald-700" : "bg-emerald-500 hover:bg-emerald-600"} ${addDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
        title={addDisabled ? "Manca email o telefono" : isFid ? "Registra spesa + cashback" : "Iscrivi + registra spesa"}
        data-testid={`customer-add-purchase-${c.key}`}
      >
        € +
      </button>
      {isFid && (
        <button
          onClick={() => onAddCredit?.(c)}
          className={`${pill} px-2 py-1 bg-[#C48B31] hover:bg-[#a9741f] text-white`}
          title="Carica un credito (buono / omaggio) sulla tessera: si usa a pezzi fino a esaurimento"
          data-testid={`customer-add-credit-${c.key}`}
        >
          Buono
        </button>
      )}
      <button
        onClick={() => onRedeemCredit?.(c)}
        disabled={!canRedeem}
        className={`${pill} px-2 py-1 ${canRedeem ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-neutral-200 text-neutral-500 cursor-not-allowed"}`}
        title={canRedeem ? `Usa credito Fidelity (€ ${(balCents/100).toFixed(2)} disponibili)` : "Nessun credito disponibile"}
        data-testid={`customer-redeem-credit-${c.key}`}
      >
        € −
      </button>
      <button
        onClick={() => onWhatsApp(c)}
        disabled={!c.phone || c.do_not_contact}
        className={`${pill} ${c.wa_count > 0 ? "bg-purple-600 hover:bg-purple-700" : "bg-emerald-600 hover:bg-emerald-700"} disabled:bg-neutral-200 disabled:text-neutral-500 text-white px-2 py-1`}
        title={c.do_not_contact ? "Cliente in Non-Contattare" : c.wa_count > 0 ? `WhatsApp già inviato (${c.wa_count}) · clicca per inviarne un altro` : c.phone ? "WhatsApp generico" : "Numero non disponibile"}
        data-testid={`customer-wa-${c.key}`}
      >
        <MessageCircle className="h-3 w-3" />
      </button>
      <button
        onClick={() => onWhatsApp(c, "promo")}
        disabled={!c.phone || c.do_not_contact}
        className={`${pill} bg-[#4a1c17] hover:bg-[#2b1410] disabled:bg-neutral-200 disabled:text-neutral-500 text-white px-2 py-1`}
        title={c.do_not_contact ? "Cliente in Non-Contattare" : c.phone ? "Invia messaggio promo" : "Numero non disponibile"}
        data-testid={`customer-wa-promo-${c.key}`}
      >
        Promo
      </button>
      <button
        onClick={() => onFidelityWelcome?.(c)}
        disabled={!c.phone || c.do_not_contact || !c.name}
        className={`${pill} bg-amber-600 hover:bg-amber-700 disabled:bg-neutral-200 disabled:text-neutral-500 text-white px-2 py-1 gap-1`}
        title={c.do_not_contact ? "Cliente in Non-Contattare" : !c.name ? "Aggiungi il nome" : c.phone ? "Benvenuto Fidelity (+10€ + WhatsApp)" : "Numero non disponibile"}
        data-testid={`customer-wa-fidelity-${c.key}`}
      >
        <Gift className="h-3 w-3" /> 10€
      </button>

      {/* Secondary actions dropdown — highlighted so it's easy to find. */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center justify-center rounded-full border-2 h-7 w-7 transition-colors ${
            open
              ? "border-[#4a1c17] bg-[#4a1c17] text-white"
              : "border-[#4a1c17] text-[#4a1c17] hover:bg-[#4a1c17] hover:text-white"
          }`}
          title="Altre azioni: Cronologia · Modifica dati · Elimina Fidelity · Non-Contattare"
          data-testid={`customer-more-${c.key}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
        {open && (
          <div
            className="absolute right-0 top-8 z-30 min-w-[180px] rounded-xl border border-[#e8dcc8] bg-white shadow-lg overflow-hidden"
            data-testid={`customer-more-menu-${c.key}`}
          >
            <button onClick={() => { setOpen(false); onDetail(c); }} className={menuBtn} data-testid={`customer-detail-${c.key}`}>
              <Eye className="h-4 w-4 text-[#7c5a4e]" /> Cronologia
            </button>
            <button
              onClick={() => { setOpen(false); onToggleDnc(c); }}
              className={menuBtn}
              data-testid={`customer-dnc-toggle-${c.key}`}
            >
              {c.do_not_contact ? <ShieldOff className="h-4 w-4 text-emerald-700" /> : <BanIcon className="h-4 w-4 text-red-600" />}
              {c.do_not_contact ? "Riabilita contatti" : "Non contattare"}
            </button>
            <div className="border-t border-[#e8dcc8]" />
            <GdprMenuItems c={c} className={menuBtn} onDone={(changed) => { setOpen(false); if (changed) window.dispatchEvent(new Event("customers:refresh")); }} />
          </div>
        )}
      </div>
    </div>
  );
}

function SourcesTable({ rows, total, onWhatsApp, editRow, onStartEdit, onCancelEdit, onSaveEdit, onDelete, onCancelBooking }) {
  if (rows.length === 0) {
    return <div className="rounded-2xl border border-dashed border-[#e8dcc8] p-10 text-center text-sm text-[#7c5a4e]">Nessuna riga trovata.</div>;
  }
  return (
    <div className="rounded-2xl border border-[#e8dcc8] bg-white overflow-hidden">
      <div className="px-3 py-2 bg-[#faf3ee] text-[10px] uppercase tracking-wider text-[#7c5a4e] font-semibold flex justify-between">
        <span>{rows.length} riga/e · una per iscrizione o prenotazione</span>
        {total !== rows.length && <span>filtrati da {total}</span>}
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[#7c5a4e] uppercase text-[10px] tracking-wider">
          <tr>
            <th className="text-left px-3 py-2">Fonte</th>
            <th className="text-left px-3 py-2">Data</th>
            <th className="text-left px-3 py-2">Cliente</th>
            <th className="text-left px-3 py-2 hidden md:table-cell">Contatti</th>
            <th className="text-center px-3 py-2 hidden sm:table-cell">Pers.</th>
            <th className="text-right px-3 py-2">Importo</th>
            <th className="text-center px-3 py-2 hidden sm:table-cell">Timbri</th>
            <th className="text-right px-3 py-2">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <React.Fragment key={r.id}>
            <tr className="border-t border-[#e8dcc8]" data-testid={`source-row-${r.id}`}>
              <td className="px-3 py-2"><SourceBadge source={r.source} /></td>
              <td className="px-3 py-2 text-[11px] text-[#7c5a4e] whitespace-nowrap">{fmtDate(r.date)}</td>
              <td className="px-3 py-2">
                <div className="font-semibold text-[#2b1410]">{r.name || <em className="text-[#7c5a4e]">(senza nome)</em>}</div>
                <div className="text-[11px] text-[#7c5a4e] md:hidden">{r.email || r.phone}</div>
              </td>
              <td className="px-3 py-2 hidden md:table-cell text-[11px] text-[#7c5a4e]">
                {r.email && <div className="truncate max-w-[200px]">{r.email}</div>}
                {r.phone && <div>📞 {r.phone}</div>}
              </td>
              <td className="px-3 py-2 text-center hidden sm:table-cell">{r.num_people || "—"}</td>
              <td className="px-3 py-2 text-right font-mono text-[#4a1c17]">{r.amount ? fmtMoney(r.amount) : "—"}</td>
              <td className="px-3 py-2 text-center hidden sm:table-cell">
                {r.stamps > 0 ? <span className="font-mono font-bold text-amber-700">{r.stamps}</span> : <span className="text-[#7c5a4e]">—</span>}
              </td>
              <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                <button
                  onClick={() => onStartEdit(editRow?.id === r.id ? null : r)}
                  data-testid={`row-edit-${r.id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] text-[#4a1c17] text-[11px] px-2 py-1.5 hover:bg-[#faf3ee]"
                  title="Modifica"
                >
                  <Pencil className="h-3 w-3" />
                </button>
                {(r.source === "spa" || r.source === "massaggi") && r.detail?.status !== "cancelled" && (
                  <button
                    onClick={() => onCancelBooking(r)}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-200 text-amber-700 text-[11px] px-2 py-1.5 hover:bg-amber-50"
                    title="Annulla prenotazione"
                  >
                    ✕
                  </button>
                )}
                <button
                  onClick={() => onWhatsApp(r)}
                  disabled={!r.phone}
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-neutral-200 disabled:text-neutral-500 text-white text-[11px] px-2 py-1.5"
                  title="WhatsApp"
                >
                  <MessageCircle className="h-3 w-3" />
                </button>
                <button
                  onClick={() => onDelete(r)}
                  data-testid={`row-delete-${r.id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-red-200 text-red-700 text-[11px] px-2 py-1.5 hover:bg-red-50"
                  title="Elimina"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </td>
            </tr>
            {editRow?.id === r.id && (
              <tr className="bg-[#faf3ee]">
                <td colSpan={8} className="px-3 py-3">
                  <EditRowForm row={r} onCancel={onCancelEdit} onSave={onSaveEdit} />
                </td>
              </tr>
            )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function EditRowForm({ row, onCancel, onSave }) {
  const [name, setName] = useState(row.name || "");
  const [email, setEmail] = useState(row.email || "");
  const [phone, setPhone] = useState(row.phone || "");
  const [notes, setNotes] = useState(row.detail?.notes || "");
  const [numPeople, setNumPeople] = useState(row.num_people || 1);
  const [date, setDate] = useState(row.date || "");
  const [busy, setBusy] = useState(false);
  const isBooking = row.source === "spa" || row.source === "massaggi";

  const submit = async () => {
    setBusy(true);
    const patch = { name, email, phone };
    if (isBooking) {
      patch.notes = notes;
      patch.num_people = Number(numPeople);
      patch.date = date;
    }
    await onSave(row, patch);
    setBusy(false);
  };

  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-[#7c5a4e] font-semibold">
        Modifica {SOURCE_META[row.source]?.label} · {row.name}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="text-xs text-[#2b1410]">
          <div className="text-[10px] uppercase text-[#7c5a4e]">Nome</div>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-[#e8dcc8] px-2 py-1.5 bg-white" />
        </label>
        <label className="text-xs text-[#2b1410]">
          <div className="text-[10px] uppercase text-[#7c5a4e]">Email</div>
          <input value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg border border-[#e8dcc8] px-2 py-1.5 bg-white" />
        </label>
        <label className="text-xs text-[#2b1410]">
          <div className="text-[10px] uppercase text-[#7c5a4e]">Telefono</div>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-lg border border-[#e8dcc8] px-2 py-1.5 bg-white" />
        </label>
        {isBooking && (
          <>
            <label className="text-xs text-[#2b1410]">
              <div className="text-[10px] uppercase text-[#7c5a4e]">Data</div>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full rounded-lg border border-[#e8dcc8] px-2 py-1.5 bg-white" />
            </label>
            <label className="text-xs text-[#2b1410]">
              <div className="text-[10px] uppercase text-[#7c5a4e]">Persone</div>
              <input type="number" min={1} value={numPeople} onChange={(e) => setNumPeople(e.target.value)} className="w-full rounded-lg border border-[#e8dcc8] px-2 py-1.5 bg-white" />
            </label>
            <label className="text-xs text-[#2b1410] sm:col-span-3">
              <div className="text-[10px] uppercase text-[#7c5a4e]">Note</div>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-lg border border-[#e8dcc8] px-2 py-1.5 bg-white" />
            </label>
          </>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="rounded-full border border-[#e8dcc8] text-[#7c5a4e] text-xs px-3 py-1.5">Annulla</button>
        <button onClick={submit} disabled={busy} data-testid={`row-save-${row.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4 py-1.5 disabled:opacity-50">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Salva
        </button>
      </div>
    </div>
  );
}

function DetailModal({ customer, onClose, editRow, onStartEdit, onCancelEdit, onSaveEdit, onDelete, onCancelBooking }) {
  const bookings = customer.rows.filter((r) => r.source !== "fidelity")
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const fidelityRow = customer.rows.find((r) => r.source === "fidelity");
  const transactions = (fidelityRow?.detail?.transactions || [])
    .slice()
    .sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
  const redemptions = fidelityRow?.detail?.redemptions || [];
  const balanceEuros = ((fidelityRow?.detail?.balance_cents || 0) / 100);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="customer-detail-modal"
      >
        <div className="sticky top-0 bg-[#4a1c17] text-white px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-serif text-lg font-bold">{customer.name || "Cliente senza nome"}</div>
            <div className="text-xs text-[#f7ebdf]/80">
              {customer.email || ""} {customer.phone && `· 📞 ${customer.phone}`}
            </div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatCard label="Visite" value={customer.visits} />
            <StatCard label="Spesa totale" value={fmtMoney(customer.total_spend)} />
            <StatCard label="Credito" value={`€ ${balanceEuros.toFixed(2)}`} tone={balanceEuros > 0 ? "emerald" : undefined} />
            <StatCard label="Livello" value={customer.level_it || "—"} tone="amber" />
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-[#7c5a4e] font-semibold mb-2">Fonti</div>
            <div className="flex items-center gap-2 flex-wrap">
              {customer.sources.map((s) => <SourceBadge key={s} source={s} />)}
              {fidelityRow && (
                <>
                  <button
                    onClick={() => onStartEdit(editRow?.id === fidelityRow.id ? null : fidelityRow)}
                    className="ml-2 inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] text-[#4a1c17] text-[11px] px-2 py-1 hover:bg-[#faf3ee]"
                    title="Modifica iscritto Fidelity"
                  >
                    <Pencil className="h-3 w-3" /> Fidelity
                  </button>
                  <button
                    onClick={() => onDelete(fidelityRow)}
                    className="inline-flex items-center gap-1 rounded-full border border-red-200 text-red-700 text-[11px] px-2 py-1 hover:bg-red-50"
                    title="Elimina iscrizione Fidelity"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </>
              )}
            </div>
            {editRow?.id === fidelityRow?.id && (
              <div className="mt-2 rounded-lg border border-[#e8dcc8] bg-[#faf3ee] p-3">
                <EditRowForm row={fidelityRow} onCancel={onCancelEdit} onSave={onSaveEdit} />
              </div>
            )}
          </div>

          {bookings.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-[#7c5a4e] font-semibold mb-2">Prenotazioni</div>
              <div className="rounded-lg border border-[#e8dcc8] overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#faf3ee] text-[#7c5a4e] uppercase text-[10px]">
                    <tr>
                      <th className="text-left px-2 py-1.5">Fonte</th>
                      <th className="text-left px-2 py-1.5">Data</th>
                      <th className="text-center px-2 py-1.5">Pers.</th>
                      <th className="text-right px-2 py-1.5">Importo</th>
                      <th className="text-center px-2 py-1.5">Stato</th>
                      <th className="text-right px-2 py-1.5">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <React.Fragment key={b.id}>
                      <tr className="border-t border-[#e8dcc8]">
                        <td className="px-2 py-1.5"><SourceBadge source={b.source} /></td>
                        <td className="px-2 py-1.5">{fmtDate(b.date)}</td>
                        <td className="px-2 py-1.5 text-center">{b.num_people}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(b.amount)}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] ${b.detail?.status === "cancelled" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>
                            {b.detail?.status === "cancelled" ? "Annullata" : "Confermata"}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-right space-x-1 whitespace-nowrap">
                          <button
                            onClick={() => onStartEdit(editRow?.id === b.id ? null : b)}
                            className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] text-[#4a1c17] text-[10px] px-1.5 py-1 hover:bg-[#faf3ee]"
                            title="Modifica"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          {b.detail?.status !== "cancelled" && (
                            <button
                              onClick={() => onCancelBooking(b)}
                              className="inline-flex items-center gap-1 rounded-full border border-amber-200 text-amber-700 text-[10px] px-1.5 py-1 hover:bg-amber-50"
                              title="Annulla"
                            >✕</button>
                          )}
                          <button
                            onClick={() => onDelete(b)}
                            className="inline-flex items-center gap-1 rounded-full border border-red-200 text-red-700 text-[10px] px-1.5 py-1 hover:bg-red-50"
                            title="Elimina"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                      {editRow?.id === b.id && (
                        <tr className="bg-[#faf3ee]">
                          <td colSpan={6} className="px-2 py-2">
                            <EditRowForm row={b} onCancel={onCancelEdit} onSave={onSaveEdit} />
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {transactions.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-[#7c5a4e] font-semibold mb-2">Movimenti cashback</div>
              <div className="rounded-lg border border-[#e8dcc8] overflow-hidden max-h-72 overflow-y-auto" data-testid="cashback-history">
                <table className="w-full text-xs">
                  <thead className="bg-[#faf3ee] text-[#7c5a4e] uppercase text-[10px] sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Data</th>
                      <th className="text-left px-2 py-1.5">Tipo</th>
                      <th className="text-right px-2 py-1.5">Spesa</th>
                      <th className="text-right px-2 py-1.5">Cashback</th>
                      <th className="text-left px-2 py-1.5">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t, i) => {
                      const type = t.type || "earn";
                      const isEarn = type === "earn";
                      const isWelcome = type === "welcome";
                      const isRedeem = type === "redeem";
                      const cashCents = Number(t.cashback_cents || 0);
                      const redeemedCents = Number(t.redeemed_cents || 0);
                      const spentCents = Number(t.amount_spent_cents || 0);
                      return (
                        <tr key={i} className="border-t border-[#e8dcc8]">
                          <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(t.ts)}</td>
                          <td className="px-2 py-1.5">
                            {isWelcome && <span className="inline-block rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold">🎁 Benvenuto</span>}
                            {isEarn && <span className="inline-block rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold">+ Cashback {t.cashback_pct ? `${t.cashback_pct}%` : ""}</span>}
                            {isRedeem && <span className="inline-block rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-[10px] font-semibold">− Utilizzo</span>}
                            {!isEarn && !isWelcome && !isRedeem && <span className="text-[#7c5a4e]">{type}</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-[#7c5a4e]">
                            {isEarn && spentCents > 0 ? `€ ${(spentCents / 100).toFixed(2)}` : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono font-bold ${isRedeem ? "text-red-700" : "text-emerald-700"}`}>
                            {isRedeem
                              ? `− € ${(redeemedCents / 100).toFixed(2)}`
                              : cashCents > 0
                                ? `+ € ${(cashCents / 100).toFixed(2)}`
                                : "—"}
                          </td>
                          <td className="px-2 py-1.5 text-[#7c5a4e] truncate max-w-[180px]" title={t.note || ""}>{t.note || ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {redemptions.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-[#7c5a4e] font-semibold mb-2">Esperienze richieste</div>
              <ul className="text-xs text-[#2b1410] space-y-1">
                {redemptions.map((r) => (
                  <li key={r.id}>· {r.name_it} <span className="text-[#7c5a4e]">({fmtDate(r.ts)} · {r.status || "pending"})</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const TEMPLATE_LABELS = {
  customer_generic: "Messaggio generico",
  promo: "Promo",
  fidelity_code: "Fidelity",
  spa_booking: "Spa",
  massage_booking: "Massaggio",
  room_pin_guest: "Codice camera",
  ttlock_guest: "PIN TTLock",
  ttlock_admin: "PIN admin",
};

function relTime(iso) {
  if (!iso) return "";
  const now = Date.now();
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return `${s}s fa`;
  if (s < 3600) return `${Math.floor(s / 60)}m fa`;
  if (s < 86400) return `${Math.floor(s / 3600)}h fa`;
  return `${Math.floor(s / 86400)}g fa`;
}

// Campaign performance widget — shows how many WhatsApp messages have
// been sent per template in the last N days so the owner can gauge at a
// glance whether a promo is still fresh or worth firing again.
function WaCampaignWidget() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchWaStats(days)
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => { if (!cancelled) setData({ total: 0, items: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const promo = (data?.items || []).find((i) => i.template_key === "promo");
  const topOther = (data?.items || []).filter((i) => i.template_key !== "promo").slice(0, 3);

  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#2b1410] to-[#4a1c17] text-[#f7ebdf] p-4" data-testid="wa-campaign-widget">
      <div className="flex flex-wrap items-start gap-4">
        {/* Headline: most recent promo */}
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-[#f7ebdf]/70 mb-1">
            <Megaphone className="h-3.5 w-3.5" /> Ultima campagna
          </div>
          {loading ? (
            <div className="text-sm text-[#f7ebdf]/60">Caricamento…</div>
          ) : promo ? (
            <div>
              <div className="text-2xl font-serif font-bold leading-tight" data-testid="wa-campaign-promo-count">
                {promo.count} <span className="text-sm font-sans font-normal opacity-80">invii · ultimi {days} giorni</span>
              </div>
              <div className="text-xs text-[#f7ebdf]/70 mt-1">
                {promo.unique_recipients} destinatari unici · ultimo {relTime(promo.last_sent)}
              </div>
            </div>
          ) : (
            <div>
              <div className="text-2xl font-serif font-bold leading-tight opacity-70">Nessuna promo</div>
              <div className="text-xs text-[#f7ebdf]/70 mt-1">Modifica il template in <em>Messaggi → Promo</em> e usa il bottone “Promo” qui sotto.</div>
            </div>
          )}
        </div>

        {/* Other templates */}
        {!loading && topOther.length > 0 && (
          <div className="flex flex-wrap gap-3 max-w-full">
            {topOther.map((it) => (
              <div key={it.template_key} className="bg-[#f7ebdf]/10 rounded-lg px-3 py-2 min-w-[120px]" data-testid={`wa-campaign-item-${it.template_key}`}>
                <div className="text-[10px] uppercase tracking-wide text-[#f7ebdf]/60">
                  {TEMPLATE_LABELS[it.template_key] || it.template_key}
                </div>
                <div className="font-semibold flex items-baseline gap-1">
                  {it.count}
                  <span className="text-[10px] font-normal text-[#f7ebdf]/60">invii</span>
                </div>
                <div className="text-[10px] text-[#f7ebdf]/60">{relTime(it.last_sent)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Range selector */}
        <div className="flex items-center gap-1 self-start">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              data-testid={`wa-campaign-range-${d}`}
              className={`text-[11px] px-2 py-1 rounded-full border transition ${
                d === days
                  ? "bg-[#f7ebdf] text-[#4a1c17] border-[#f7ebdf]"
                  : "border-[#f7ebdf]/30 text-[#f7ebdf]/80 hover:border-[#f7ebdf]/60"
              }`}
            >
              {d}g
            </button>
          ))}
        </div>
      </div>

      {!loading && data?.total > 0 && (
        <div className="mt-3 flex items-center gap-1 text-[11px] text-[#f7ebdf]/70">
          <TrendingUp className="h-3 w-3" />
          <span data-testid="wa-campaign-total">{data.total} messaggi totali in {days} giorni</span>
        </div>
      )}
    </div>
  );
}

